"use client";

import { useEffect, useRef } from "react";
import { useSession } from "@/lib/auth/auth-client";
import {
  initAmplitude,
  identifyUser,
  identifyUserProperties,
  identifyUserSetOnce,
  isAmplitudeInitialized,
  refreshAmplitudeExperiments,
  resetAmplitudeUser,
  setAmplitudePlatformContext,
  setUserGroup,
  startSessionReplay,
  flushAmplitude,
  trackEvent,
} from "@/lib/analytics/amplitude";
import { getClientPlatformContext } from "@/lib/analytics/platform-context";
import { LOGIN_COMPLETED, LOGOUT } from "@/lib/analytics/amplitude-events";
import { reportClientError } from "@/lib/observability/report-client-error";
import { setGtagUserId } from "@/lib/analytics/gtagLoader";

/**
 * Initializes Amplitude on mount and syncs user identity
 * with Better Auth session state.
 *
 * Also handles:
 * - Page unload flushing to prevent event loss
 * - User properties enrichment (plan tier, language, channels)
 * - Group analytics for plan-level segmentation
 * - Auth lifecycle events (login/logout)
 */
export function AmplitudeProvider({ children }: { children: React.ReactNode }) {
  const { data: session } = useSession();
  const prevUserId = useRef<string | null>(null);
  const sessionRef = useRef(session);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  // Phase 1: SDK init + platform context, then flush session (avoids Engagement
  // "not booted" when the session effect runs before initAll completes).
  useEffect(() => {
    // Skip Amplitude in Electron — web analytics not needed in desktop app,
    // and the init adds unnecessary network overhead.
    if (typeof window !== "undefined" && window.electronAPI !== undefined) {
      return;
    }

    let cancelled = false;

    const init = async () => {
      try {
        await initAmplitude();
        if (cancelled) return;

        // Set platform/OS/browser/device as Amplitude user properties
        // so all events carry this context for segmentation.
        const platformCtx = getClientPlatformContext();
        setAmplitudePlatformContext(platformCtx);

        syncAmplitudeSessionFromAuthSession(sessionRef.current, prevUserId);
      } catch (err) {
        // Analytics init is non-critical — app works without it.
        // Primary reporting happens inside initAmplitude(); this is
        // a safety net for errors thrown after initAll completes
        // (e.g. setAmplitudePlatformContext).
        reportClientError(err, {
          source: "providers.AmplitudeProvider",
          feature: "analytics",
          action: "init",
          severity: "warning",
        });
      }
    };

    void init();
    return () => {
      cancelled = true;
    };
  }, []);

  // Phase 6: Flush pending events before page unload
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleBeforeUnload = () => flushAmplitude();
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  // Phase 4 & 5: User identity sync + enrichment + auth events
  useEffect(() => {
    syncAmplitudeSessionFromAuthSession(session, prevUserId);
  }, [session]);

  return <>{children}</>;
}

type AuthSession = ReturnType<typeof useSession>["data"];

function syncAmplitudeSessionFromAuthSession(
  session: AuthSession,
  prevUserId: React.MutableRefObject<string | null>
) {
  if (!isAmplitudeInitialized()) return;

  if (session?.user) {
    const userId = session.user.id;

    // Phase 4: Enriched user properties
    identifyUser(userId, {
      email: session.user.email,
      name: session.user.name,
      signup_date: session.user.createdAt,
      image: session.user.image ? "set" : "none",
    });

    // Also set GA4 user_id so new-user attribution works across sessions
    setGtagUserId(userId);

    const createdRaw = session.user.createdAt;
    const createdIso =
      typeof createdRaw === "string"
        ? createdRaw
        : createdRaw instanceof Date
          ? createdRaw.toISOString()
          : "";
    if (createdIso.length >= 7) {
      identifyUserSetOnce({
        signup_cohort_month: createdIso.slice(0, 7),
      });
    }

    const isNewAmplitudeUser = prevUserId.current !== userId;

    // Phase 5: Track login event (only on new session, not on page reload)
    if (isNewAmplitudeUser) {
      if (prevUserId.current !== null) {
        // User switched — the prior user effectively logged out
        trackEvent(LOGOUT);
      }
      trackEvent(LOGIN_COMPLETED, {
        method: "session_restore",
      });
    }
    prevUserId.current = userId;

    // SENTRY FIX: VIDSEEDS-2C6 — enrichment fetches auth/profile analytics
    // traits and must run once per user, not on every session object refresh.
    if (isNewAmplitudeUser) {
      // Phase 4: Fetch additional user traits asynchronously (plan tier, channels)
      void enrichUserProperties(userId);
    }

    void startSessionReplay();
  } else {
    if (prevUserId.current !== null) {
      trackEvent(LOGOUT);
      prevUserId.current = null;
    }
    resetAmplitudeUser();
  }
}

/**
 * Fetch additional user data and set as Amplitude user properties + groups.
 * Non-blocking — failures are silently ignored.
 */
async function enrichUserProperties(userId: string) {
  try {
    const res = await fetch("/api/auth/me", { credentials: "include" });
    if (!res.ok) return;
    const data = await res.json();

    const traits: Record<string, unknown> = {};
    if (typeof data.channelCount === "number") {
      traits.connected_channels = data.channelCount;
      traits.has_connected_channel = data.channelCount > 0;
    }
    if (data.subscription?.packName) {
      traits.plan_tier = data.subscription.packName;
      setUserGroup("plan", data.subscription.packName);
    } else {
      traits.plan_tier = "free";
      setUserGroup("plan", "free");
    }

    if (data.activeChannelId && String(data.activeChannelId).length >= 5) {
      setUserGroup("active_channel", String(data.activeChannelId));
    } else {
      setUserGroup("active_channel", "none");
    }

    if (Object.keys(traits).length > 0) {
      identifyUser(userId, traits);
    }

    await enrichCohortProperties(userId, data);

    await refreshAmplitudeExperiments();
  } catch (err) {
    // Best-effort enrichment — don't break the app if it fails
    console.error("[Amplitude] User enrichment failed:", err);
    reportClientError(err, {
      source: "component.AmplitudeProvider",
      feature: "analytics",
      action: "enrichUserProperties",
      component: "AmplitudeProvider",
      handled: true,
    });
  }
}

/**
 * Set cohort/retention user properties for conversion analysis.
 * Non-blocking.
 */
async function enrichCohortProperties(
  userId: string,
  userData: Record<string, unknown>
) {
  try {
    // Calculate days since signup from createdAt
    const createdAt = userData.createdAt as string | undefined;
    if (createdAt) {
      const createdDate = new Date(createdAt);
      const now = new Date();
      const daysSinceSignup = Math.floor(
        (now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24)
      );
      identifyUserSetOnce({
        days_since_signup: daysSinceSignup,
      });
    }

    // Track signup source from UTM stored on user record
    if (userData.signupSource) {
      identifyUserSetOnce({
        signup_source: String(userData.signupSource),
      });
    }

    if (typeof userData.totalProjects === "number") {
      // SENTRY FIX: VIDSEEDS-2C6 — use `/api/auth/me`'s exact count instead
      // of fetching `/api/projects/list/?limit=100` from global analytics.
      identifyUserProperties({
        total_projects_created: userData.totalProjects,
      });
    }
  } catch (err) {
    reportClientError(err, {
      source: "component.AmplitudeProvider",
      feature: "analytics",
      action: "enrichCohortProperties",
      component: "AmplitudeProvider",
      handled: true,
    });
  }
}
